<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div>
        <h5 class="fw-bold mb-0">
          <i class="fa-solid fa-motorcycle me-2" style="color: #00B14F;"></i>{{ $t('_delivery.Platform Sales') }}
        </h5>
        <small class="text-muted">{{ $t('Sales') }}</small>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

      <!-- Summary cards -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-lg">
          <div class="card border-0 shadow-sm h-100"><div class="card-body py-3">
            <div class="text-muted small">{{ $t('_delivery.Gross') }}</div>
            <div class="fw-bold fs-5">{{ formatCurrency(summary.gross) }}</div>
          </div></div>
        </div>
        <div class="col-6 col-lg">
          <div class="card border-0 shadow-sm h-100"><div class="card-body py-3">
            <div class="text-muted small">{{ $t('_delivery.GP') }}</div>
            <div class="fw-bold fs-5 text-danger">-{{ formatCurrency(summary.commission) }}</div>
          </div></div>
        </div>
        <div class="col-6 col-lg">
          <div class="card border-0 shadow-sm h-100"><div class="card-body py-3">
            <div class="text-muted small">{{ $t('_delivery.Net payout') }}</div>
            <div class="fw-bold fs-5">{{ formatCurrency(summary.net_payout) }}</div>
          </div></div>
        </div>
        <div class="col-6 col-lg">
          <div class="card border-0 shadow-sm h-100"><div class="card-body py-3">
            <div class="text-muted small"><i class="fa-solid fa-hourglass-half text-warning me-1"></i>{{ $t('_delivery.Awaiting payout') }}</div>
            <div class="fw-bold fs-5 text-warning">{{ formatCurrency(summary.pending_amount) }}</div>
          </div></div>
        </div>
        <div class="col-6 col-lg">
          <div class="card border-0 shadow-sm h-100"><div class="card-body py-3">
            <div class="text-muted small"><i class="fa-solid fa-circle-check text-success me-1"></i>{{ $t('_delivery.Received') }}</div>
            <div class="fw-bold fs-5 text-success">{{ formatCurrency(summary.received_amount) }}</div>
          </div></div>
        </div>
      </div>

      <!-- Chart -->
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-header bg-white border-0 py-3">
          <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-chart-bar me-2 text-success"></i>{{ $t('_delivery.Gross by platform') }}</h6>
        </div>
        <div class="card-body pt-0">
          <apexchart ref="platformChart" type="bar" height="300" :options="chartOptions" :series="chartSeries" />
        </div>
      </div>

      <!-- Table -->
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover table-sm mb-0 align-middle">
              <thead class="table-light">
                <tr>
                  <th class="ps-3">{{ $t('_delivery.Channel') }}</th>
                  <th class="text-end">{{ $t('Orders') }}</th>
                  <th class="text-end">{{ $t('_delivery.Gross') }}</th>
                  <th class="text-end">{{ $t('_delivery.GP') }}</th>
                  <th class="text-end">{{ $t('_delivery.Net payout') }}</th>
                  <th class="text-end">{{ $t('_delivery.Awaiting payout') }}</th>
                  <th class="text-end pe-3">{{ $t('_delivery.Received') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rows.length && !loading">
                  <td colspan="7" class="text-center text-muted py-4">{{ $t('No data') }}</td>
                </tr>
                <tr v-for="p in rows" :key="p.delivery_platform_id">
                  <td class="ps-3">
                    <span class="badge me-1" :style="{ backgroundColor: p.color || '#6B7280' }">{{ p.name }}</span>
                  </td>
                  <td class="text-end">{{ p.orders }}</td>
                  <td class="text-end fw-semibold">{{ formatCurrency(p.gross) }}</td>
                  <td class="text-end text-danger">-{{ formatCurrency(p.commission) }}</td>
                  <td class="text-end">{{ formatCurrency(p.net_payout) }}</td>
                  <td class="text-end text-warning">{{ formatCurrency(p.pending_amount) }}</td>
                  <td class="text-end pe-3 text-success">{{ formatCurrency(p.received_amount) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="rows.length" class="table-light fw-bold">
                <tr>
                  <td class="ps-3">{{ $t('Total') }}</td>
                  <td class="text-end">{{ summary.orders }}</td>
                  <td class="text-end">{{ formatCurrency(summary.gross) }}</td>
                  <td class="text-end text-danger">-{{ formatCurrency(summary.commission) }}</td>
                  <td class="text-end">{{ formatCurrency(summary.net_payout) }}</td>
                  <td class="text-end text-warning">{{ formatCurrency(summary.pending_amount) }}</td>
                  <td class="text-end pe-3 text-success">{{ formatCurrency(summary.received_amount) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportPlatformSales',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Platform Sales',
      rows: [],
      summary: { orders: 0, gross: 0, commission: 0, net_payout: 0, pending_amount: 0, received_amount: 0 },
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.platformSales(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.rows = payload.platforms || [];
      this.summary = payload.summary || this.summary;
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const chartImg = await this.getChartImage('platformChart');
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('_delivery.Platform Sales'), style: 'sectionTitle' },
      ];
      if (chartImg) {
        content.push({ image: chartImg, width: 500, margin: [0, 0, 0, 12] });
      }
      content.push({
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [
              { text: this.$t('_delivery.Channel'), style: 'tableHeader' },
              { text: this.$t('Orders'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('_delivery.Gross'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('_delivery.GP'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('_delivery.Net payout'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('_delivery.Awaiting payout'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('_delivery.Received'), style: 'tableHeader', alignment: 'right' },
            ],
            ...this.rows.map(p => [
              { text: p.name, style: 'tableCell' },
              { text: String(p.orders), style: 'tableCell', alignment: 'right' },
              { text: this.formatCurrencyRaw(p.gross), style: 'tableCell', alignment: 'right' },
              { text: '-' + this.formatCurrencyRaw(p.commission), style: 'tableCell', alignment: 'right' },
              { text: this.formatCurrencyRaw(p.net_payout), style: 'tableCell', alignment: 'right' },
              { text: this.formatCurrencyRaw(p.pending_amount), style: 'tableCell', alignment: 'right' },
              { text: this.formatCurrencyRaw(p.received_amount), style: 'tableCell', alignment: 'right' },
            ]),
            [
              { text: this.$t('Total'), style: 'tableHeader' },
              { text: String(this.summary.orders), style: 'tableHeader', alignment: 'right' },
              { text: this.formatCurrencyRaw(this.summary.gross), style: 'tableHeader', alignment: 'right' },
              { text: '-' + this.formatCurrencyRaw(this.summary.commission), style: 'tableHeader', alignment: 'right' },
              { text: this.formatCurrencyRaw(this.summary.net_payout), style: 'tableHeader', alignment: 'right' },
              { text: this.formatCurrencyRaw(this.summary.pending_amount), style: 'tableHeader', alignment: 'right' },
              { text: this.formatCurrencyRaw(this.summary.received_amount), style: 'tableHeader', alignment: 'right' },
            ],
          ],
        },
        layout: 'lightHorizontalLines',
      });
      return { content };
    },
  },
  computed: {
    chartSeries() {
      return [{ name: this.$t('_delivery.Gross'), data: this.rows.map(p => p.gross || 0) }];
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        xaxis: { categories: this.rows.map(p => p.name) },
        yaxis: { labels: { formatter: v => this.formatCompact(v) } },
        colors: ['#00B14F'],
        plotOptions: { bar: { borderRadius: 6, columnWidth: '50%', distributed: true } },
        legend: { show: false },
        dataLabels: { enabled: true, formatter: v => this.formatCompact(v), style: { fontSize: '12px' } },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
        grid: { borderColor: '#f1f5f9' },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
